import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AdminLesson } from '../lib/types';
import { api } from '../lib/api';
import { BlockEditor } from './BlockEditor';
import { Banner, Area, Field, IconAction, Num, Select, Text, Toggle, icons, slugify } from './ui';
import { useAdminContent } from './useAdminContent';
import { EditModal } from './EditModal';
import { useConfirm } from '../components/ConfirmDialog';
import { Loading } from '../components/Loading';

/** Lessons within one track: order, draft state, and the block editor. */
export function LessonsAdmin() {
  const { trackId = '' } = useParams();
  const { bundle, loading, error, note, run } = useAdminContent();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState<Partial<AdminLesson> | null>(null);

  if (loading) return <Loading />;
  if (!bundle) return <Banner error={error ?? 'Could not load content.'} />;

  const track = bundle.tracks.find((t) => t._id === trackId);
  if (!track) return <Banner error="That track no longer exists." />;

  const lessons = bundle.lessons.filter((l) => l.trackId === trackId);

  // Reordering writes straight through — there is no draft to discard — so it asks
  // first, like every other write in the console.
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= lessons.length) return;
    const ids = lessons.map((l) => l._id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    void (async () => {
      const ok = await confirm({
        title: `Move “${lessons[i].title}” ${d < 0 ? 'up' : 'down'}?`,
        body: 'Lesson order sets the reading order in the track, and the lesson the dashboard offers next. This saves as soon as you confirm.',
        confirmLabel: 'Save new order',
      });
      if (ok) await run(() => api.reorderLessons(ids));
    })();
  };

  async function save() {
    if (!editing?.title?.trim()) return;

    const confirmed = await confirm({
      title: editing._id ? 'Save changes to this lesson?' : 'Create this lesson?',
      body: editing.published ?? true
        ? 'It is marked visible, so vendors will see the change immediately.'
        : 'It is a draft, so vendors will not see it until you publish.',
      confirmLabel: editing._id ? 'Save changes' : 'Create lesson',
    });
    if (!confirmed) return;

    const body = {
      // A lesson can be moved to another track from the form, so trackId comes from the
      // draft and only falls back to the track being viewed.
      trackId: editing.trackId ?? trackId,
      slug: editing.slug?.trim() || slugify(editing.title),
      title: editing.title.trim(),
      mins: editing.mins && editing.mins > 0 ? editing.mins : 5,
      summary: editing.summary ?? '',
      blocks: editing.blocks ?? [],
      published: editing.published ?? true,
    };
    const ok = editing._id
      ? await run(() => api.updateLesson(editing._id!, body), 'Lesson saved.')
      : await run(() => api.createLesson(body), 'Lesson created.');
    if (ok) setEditing(null);
  }

  return (
    <div className="vu-wrap">
      <Link to="/admin/tracks">← All tracks</Link>
      <div className="vu-kicker" style={{ marginTop: 'var(--space-3)' }}>{track.name}</div>
      <h1 className="vu-h1">Lessons</h1>
      <Banner error={error} note={note} />

      <div className="vu-card" style={{ padding: 0, overflow: 'hidden', marginTop: 'var(--space-5)' }}>
        <div className="vu-tablewrap">
        <table className="vu-table">
          <thead>
            <tr style={{ background: 'var(--surface-sunken)' }}>
              <th style={{ paddingLeft: 'var(--space-5)' }}>Lesson</th>
              <th style={{ width: 90 }}>Blocks</th>
              <th style={{ width: 90 }}>State</th>
              <th style={{ width: 180, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {lessons.map((l, i) => (
              <tr key={l._id}>
                <td style={{ paddingLeft: 'var(--space-5)' }}>
                  <div style={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)' }}>{l.title}</div>
                  <div className="vu-subtle" style={{ fontSize: 'var(--fs-xs)' }}>/{l.slug} · {l.mins} min</div>
                </td>
                <td>{l.blocks.length}</td>
                <td>
                  {l.published ? (
                    <span style={{ color: 'var(--green-500)', fontWeight: 600, fontSize: 'var(--fs-sm)' }}>Live</span>
                  ) : (
                    <span className="vu-subtle" style={{ fontSize: 'var(--fs-sm)' }}>Draft</span>
                  )}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                    <IconAction label="Move up" onClick={() => move(i, -1)} disabled={i === 0}>
                      {icons.up}
                    </IconAction>
                    <IconAction label="Move down" onClick={() => move(i, 1)} disabled={i === lessons.length - 1}>
                      {icons.down}
                    </IconAction>
                    <IconAction label="Edit lesson" onClick={() => setEditing(l)}>{icons.edit}</IconAction>
                    <IconAction
                      label={l.published ? 'Unpublish — hide from vendors' : 'Publish — show to vendors'}
                      onClick={() => {
                        void (async () => {
                          const ok = await confirm({
                            title: l.published ? `Hide “${l.title}” from vendors?` : `Publish “${l.title}”?`,
                            body: l.published
                              ? 'The lesson disappears from the track for every vendor. Their progress is kept.'
                              : 'The lesson becomes visible to every approved vendor immediately.',
                            confirmLabel: l.published ? 'Unpublish' : 'Publish',
                          });
                          if (ok) await run(() => api.updateLesson(l._id, { published: !l.published }));
                        })();
                      }}
                    >
                      {l.published ? icons.published : icons.hidden}
                    </IconAction>
                    <IconAction
                      label="Delete lesson"
                      danger
                      onClick={() => {
                        void (async () => {
                          const ok = await confirm({
                            title: `Delete “${l.title}”?`,
                            body: `This removes the lesson and its ${l.blocks.length} block(s) permanently. It cannot be undone.`,
                            confirmLabel: 'Delete lesson',
                            destructive: true,
                          });
                          if (ok) await run(() => api.deleteLesson(l._id));
                        })();
                      }}
                    >
                      {icons.trash}
                    </IconAction>
                  </div>
                </td>
              </tr>
            ))}
            {!lessons.length && (
              <tr>
                <td colSpan={4} style={{ paddingLeft: 'var(--space-5)' }} className="vu-muted">
                  No lessons in this track yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      <button
        type="button"
        className="vu-btn"
        style={{ marginTop: 'var(--space-5)' }}
        onClick={() => setEditing({ trackId, published: true, mins: 5, blocks: [] })}
      >
        + New lesson
      </button>

      <EditModal
        open={!!editing}
        onClose={() => setEditing(null)}
        size="lg"
        title={editing?._id ? 'Edit lesson' : 'New lesson'}
        description="Blocks render in the order below. Text is plain throughout — no HTML or Markdown, by design."
        footer={
          <>
            <button type="button" className="vu-btn vu-btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            <button type="button" className="vu-btn" onClick={save}>Save lesson</button>
          </>
        }
      >
        {editing && (
          <>
            <Field label="Title">
              <Text value={editing.title ?? ''} onChange={(title) => setEditing({ ...editing, title })} />
            </Field>
            <Field label="Slug" hint="Appears in the URL. Leave blank to derive it from the title.">
              <Text
                value={editing.slug ?? ''}
                onChange={(slug) => setEditing({ ...editing, slug })}
                placeholder={editing.title ? slugify(editing.title) : 'sizes-colours-and-variants'}
              />
            </Field>
            <Field label="Summary" hint="One line, shown on the track page and in search results.">
              <Area value={editing.summary ?? ''} onChange={(summary) => setEditing({ ...editing, summary })} rows={2} />
            </Field>
            <div className="vu-grid vu-grid-2" style={{ gap: 'var(--space-4)' }}>
              <Field label="Minutes to read">
                <Num value={editing.mins ?? 5} onChange={(mins) => setEditing({ ...editing, mins })} />
              </Field>
              <Field label="Track">
                <Select
                  value={editing.trackId ?? trackId}
                  onChange={(t) => setEditing({ ...editing, trackId: t })}
                  options={bundle.tracks.map((t) => ({ value: t._id, label: t.name }))}
                />
              </Field>
            </div>
            <Toggle
              checked={editing.published ?? true}
              onChange={(published) => setEditing({ ...editing, published })}
              label="Visible to vendors"
            />

            <h3 className="vu-h3" style={{ fontSize: 'var(--fs-h4)', margin: 'var(--space-6) 0 var(--space-3)' }}>
              Body
            </h3>
            <BlockEditor
              blocks={editing.blocks ?? []}
              onChange={(blocks) => setEditing({ ...editing, blocks })}
            />
          </>
        )}
      </EditModal>

      {dialog}
    </div>
  );
}
